import {
  BillingIcon,
  GeneralSettingsIcon,
  IntegrationsIcon,
  InvoicesIcon,
  navIconNode,
  NotificationsIcon,
  PreferencesIcon,
  ProfileIcon,
  SecurityIcon,
  TeamIcon,
  TemplatesIcon,
} from '@/components/icons';
import { Box, Flex, VStack } from '@chakra-ui/react';
import React from 'react';
import { useTranslation } from 'react-i18next';
import { useIsBrandManager } from '@/hooks/use-is-brand-manager';
import NavLink from '../../nav-bar/nav-link';
import SidebarSectionLabel from '../../nav-bar/components/SidebarSectionLabel';
import CompanionGradientSpark from '../CompanionGradientSpark';
import SidebarPanelHeader from '../SidebarPanelHeader';

interface SettingsMenuProps {
  onBack: () => void;
}

type SettingsLink = {
  to: string;
  label: string;
  icon: React.ReactNode | ((isActive: boolean) => React.ReactNode);
  enabled?: boolean;
};

type SettingsGroup = {
  label: string;
  links: SettingsLink[];
};

// The panel's rows are the main menu's rows: same height, type and icons, only
// a hair wider hitbox so the filled Companion spark sits centred with the rest.
const SETTINGS_ICON_BOX = '23px';

const SettingsMenu: React.FC<SettingsMenuProps> = ({ onBack }) => {
  const { t } = useTranslation();
  const { canManage } = useIsBrandManager();

  const groups: SettingsGroup[] = [
    {
      label: t('settings.accountNav.group'),
      links: [
        {
          to: '/settings/account/profile',
          label: t('settings.accountNav.profile'),
          icon: navIconNode(ProfileIcon),
        },
        {
          to: '/settings/account/preferences',
          label: t('settings.accountNav.preferences'),
          icon: navIconNode(PreferencesIcon),
        },
        {
          to: '/settings/account/notifications',
          label: t('settings.accountNav.notifications'),
          icon: navIconNode(NotificationsIcon),
        },
        {
          to: '/settings/account/security',
          label: t('settings.accountNav.security'),
          icon: navIconNode(SecurityIcon),
        },
      ],
    },
    {
      label: t('settings.brandWorkspaceGroup'),
      links: [
        {
          to: '/settings/general',
          label: t('settings.general'),
          icon: navIconNode(GeneralSettingsIcon),
        },
        {
          to: '/settings/agents',
          label: t('settings.agents.navTitle'),
          // AI Companion keeps its gradient spark regardless of active state:
          // the one filled glyph of the panel, the AI's own colour.
          icon: <CompanionGradientSpark />,
        },
        {
          to: '/settings/templates',
          label: t('templates.navTitle'),
          icon: navIconNode(TemplatesIcon),
          enabled: canManage,
        },
        {
          to: '/settings/integrations',
          label: t('settings.integrations'),
          icon: navIconNode(IntegrationsIcon),
        },
        {
          to: '/settings/team',
          label: t('settings.team.navTitle'),
          icon: navIconNode(TeamIcon),
        },
      ],
    },
    {
      label: t('settings.billingGroup'),
      links: [
        {
          to: '/settings/billing',
          label: t('settings.billing'),
          icon: navIconNode(BillingIcon),
        },
        {
          to: '/settings/invoices',
          label: t('settings.invoices.title'),
          icon: navIconNode(InvoicesIcon),
        },
      ],
    },
  ];

  return (
    <Flex direction="column" h="full" overflowY="hidden" gapY={4} px={3} pt={4} pb="14px">
      <Box>
        <SidebarPanelHeader title={t('settings.title')} onBack={onBack} />
      </Box>
      <Box flex={1} overflowY="auto">
        <VStack align="stretch" gap={4}>
          {groups.map((group) => {
            const links = group.links.filter((link) => link.enabled !== false);
            if (links.length === 0) return null;

            return (
              <Box key={group.label}>
                <SidebarSectionLabel>{group.label}</SidebarSectionLabel>
                <VStack align="stretch" gap="1px">
                  {links.map((link) => (
                    <NavLink
                      key={link.to}
                      label={link.label}
                      to={link.to}
                      icon={link.icon}
                      iconBoxSize={SETTINGS_ICON_BOX}
                    />
                  ))}
                </VStack>
              </Box>
            );
          })}
        </VStack>
      </Box>
    </Flex>
  );
};

export default SettingsMenu;
